import React, { useState, Fragment } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Tooltip } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';

const STATUS_MAP = {
  NONE: {
    icon: <CancelIcon sx={{ color: '#ef5350', fontSize: 16 }} />,
    labelConstant: 'cannotBeDeleted',
  },
  HARD: {
    icon: <CheckCircleIcon sx={{ color: '#66bb6a', fontSize: 16 }} />,
    labelConstant: 'hardDeleted',
  },
  SOFT: {
    icon: <CheckCircleIcon sx={{ color: '#ffeb3b', fontSize: 16 }} />,
    labelConstant: 'softDeleted',
  },
};

const StatusListRow = ({ row = {} }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const status = STATUS_MAP[row.allowedDeleteType] || STATUS_MAP.red;
  const { organizationalUnits = [] } = row;
  const { events = [] } = row;

  const hasOrgUnits = Boolean(organizationalUnits?.length);
  const hasEvents = Boolean(events?.length);
  return (
    <Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={() => setOpen(!open)}
            disabled={!hasOrgUnits && !hasOrgUnits}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.id}</TableCell>
        <TableCell
          sx={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {row.name}
        </TableCell>
        <TableCell>
          <Tooltip title={t(status.labelConstant)}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
              }}
              title={row.status}
            >
              {status.icon}
            </Box>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant='h6' gutterBottom component='div'>
                {t('relations')}
              </Typography>
              <Table size='small' aria-label='purchases'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 180 }}>
                      {t('relationType')}
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>{t('id_column')}</TableCell>
                    <TableCell>{t('name')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasOrgUnits &&
                    organizationalUnits.map((orgUnit) => (
                      <TableRow key={orgUnit.id}>
                        <TableCell component='th' scope='row'>
                          {t('orgUnit')}
                        </TableCell>
                        <TableCell>{orgUnit.id}</TableCell>
                        <TableCell>{orgUnit.name}</TableCell>
                      </TableRow>
                    ))}
                  {hasEvents &&
                    events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell component='th' scope='row'>
                          {t('event')}
                        </TableCell>
                        <TableCell>{event.id}</TableCell>
                        <TableCell>{event.name}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default function StatusListTable({ rows = [] }) {
  const { t } = useTranslation();
  return (
    <TableContainer component={Paper} sx={{ width: '100%' }}>
      <Table sx={{ tableLayout: 'fixed' }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell sx={{ width: 150 }}>{t('id_column')}</TableCell>
            <TableCell sx={{ width: 240 }}>{t('name')}</TableCell>
            <TableCell sx={{ width: 120 }}>{t('deleteStatus')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <StatusListRow row={row} key={row.id} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
